import React, { useEffect, useState } from 'react';
import Button from '~/UI/Button';
import buttonPrimaryWideStyles from '~/UI/Button/primary-wide.less';
import Heading from '~/UI/Heading/Heading';

import styles from './styles.less';
import Input from '../UI/Input/Input';
import wideInputStyles from '~/UI/Input/wide.less';
import { getPasswordReset, setNewPassword } from '~/infrastructure/api/user/passwordReset';
import { PasswordResetResponseWrapper } from 'com.cogvio.pricemonitor.api';
import isValidPassword from '~/utils/Password/isValidPassword';
import FormFieldError from '~/UI/FormFieldError/FormFieldError';
import { useHistory } from 'react-router-dom';
import Checkbox from '~/UI/Checkbox/Checkbox';
import { postGdprConsentFromPasswordReset } from '~/infrastructure/api/user/gdprConsent';

interface Props {
	token: string | null;
	userId: string | null;
}

export const InvitationAcceptPage: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ token, userId }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [storedUserId] = useState(userId);
	const [storedToken] = useState(token);
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [passwordCheckError, setPasswordCheckError] = useState<string | null>(null);
	const [password, setPassword] = useState('');
	const [passwordCheck, setPasswordCheck] = useState('');
	const [gdprConsent, setGdprConsent] = useState(false);
	const [directMarketingConsent, setDirectMarketingConsent] = useState(true);
	const [showGdprConsentWarning, setShowGdprConsentWarning] = useState(true);
	const history = useHistory();

	useEffect(() => {
		if (!storedUserId || !storedToken) {
			setError(true);
			return;
		}

		getPasswordReset(storedToken, storedUserId)
			.then((response: PasswordResetResponseWrapper) => {
				if (!response.passwordReset.active) {
					setError(true);
				}
			})
			.catch(() => {
				setError(true);
			});

		// clear query params token and user in URL
		history.replace({
			search: '',
		});
	}, []);

	const handleGdprConsent = (value: boolean) => {
		if (value) {
			setShowGdprConsentWarning(false);
		} else {
			setShowGdprConsentWarning(true);
		}

		setGdprConsent(value);
	};

	const setPasswordHandler = (value: string) => {
		setPasswordError(null);
		setPassword(value);
	};

	const setPasswordCheckHandler = (value: string) => {
		setPasswordCheckError(null);
		setPasswordCheck(value);
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!storedUserId || !storedToken) {
			setError(true);
			return;
		}

		if (!isValidPassword(password)) {
			setPasswordError('Password should be at least 8 characters long');
			return;
		} else if (password !== passwordCheck) {
			setPasswordCheckError('Password does not match. Please try again');
			return;
		}

		if (!gdprConsent) {
			return;
		}

		setLoading(true);

		postGdprConsentFromPasswordReset(
			{ termsConsent: gdprConsent, marketingConsent: directMarketingConsent },
			storedToken
		).catch(() => {
			setError(true);
		});

		setNewPassword(storedToken, storedUserId, password)
			.then(() => {
				history.push('/login');
			})
			.catch(() => {
				setError(true);
			});

		setLoading(false);
	};

	return (
		<section className={styles.section}>
			<div className={styles.in}>
				{error && (
					<div className={styles.errorMessage}>
						<Heading>Invalid request</Heading>
						<p className='mb-10'>
							Invitation was already accepted or revoked, if you did not accepted it please check your email and try it
							again.
						</p>
					</div>
				)}

				{!error && (
					<form data-testid='password-form' onSubmit={handleFormSubmit}>
						<Heading>Congratulations, you have been given access to Cogvio Price Monitor</Heading>

						<div className={styles.form}>
							<Input
								disabled={loading}
								isInvalid={passwordError !== null}
								name='password'
								placeholder='Password'
								styles={wideInputStyles}
								type='password'
								value={password}
								onChange={(event) => setPasswordHandler(event.currentTarget.value)}
							/>
							{passwordError !== null ? <FormFieldError>{passwordError}</FormFieldError> : null}

							<Input
								disabled={loading}
								isInvalid={passwordCheckError !== null}
								name='password-check'
								placeholder='Repeat password'
								styles={wideInputStyles}
								type='password'
								value={passwordCheck}
								onChange={(event) => setPasswordCheckHandler(event.currentTarget.value)}
							/>
							{passwordCheckError !== null ? <FormFieldError>{passwordCheckError}</FormFieldError> : null}

							<div className={styles.consent}>
								<div className={styles.consentCheckbox} data-testid='gdpr-consent'>
									<Checkbox checked={gdprConsent} onChange={handleGdprConsent} />
								</div>
								<div>
									<div className={styles.consentHeading}>
										Consent to the terms of the user account and information on processing personal data.
									</div>
									<div className={styles.consentText}>
										By checking the box, I agree to the terms of the user account and acknowledge the information on
										processing personal data for registration, all of which is available{' '}
										<a href={'https://www.cogvio.com/terms/'}>here</a>.
									</div>
								</div>
							</div>

							<div className={styles.consent}>
								<div className={styles.consentCheckbox} data-testid='direct-marketing-consent'>
									<Checkbox checked={directMarketingConsent} onChange={setDirectMarketingConsent} />
								</div>
								<div>
									<div className={styles.consentHeading}>Consent to direct marketing.</div>
									<div className={styles.consentText}>
										As our customer, you will receive electronic commercial communications under the conditions
										specified <a href={'https://www.cogvio.com/terms/'}>here</a>. This consent can be refused at any
										time.
									</div>
								</div>
							</div>

							{showGdprConsentWarning && (
								<div>
									<p className={styles.consentWarning}>
										If you do not wish to grant Consent to the terms of the user account and information on processing
										personal data, please contact us at <a href={`mailto:support@cogvio.com`}>support@cogvio.com</a>.
									</p>
								</div>
							)}

							<Button
								disabled={password === '' || loading || !gdprConsent}
								styles={buttonPrimaryWideStyles}
								dataTestId='accept-invitation-button'
							>
								Accept invitation
							</Button>
						</div>
					</form>
				)}
			</div>
		</section>
	);
};
